import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import tippy, { type Instance } from 'tippy.js';
import {
  MentionSuggestionList,
  type MentionItem,
  type MentionSuggestionListRef,
} from './MentionSuggestionList';

export type EntityLookup = (query: string) => MentionItem[];

/**
 * Creates a configured TipTap Mention extension for entity references.
 * The `lookupFn` is called on every keystroke after `@` to filter entity suggestions.
 */
export function createEntityMention(lookupFn: EntityLookup) {
  return Mention.configure({
    HTMLAttributes: {
      class: 'entity-mention',
      style:
        'background: rgba(25, 118, 210, 0.12); border-radius: 4px; padding: 2px 4px; font-weight: 500; color: #1976d2;',
    },
    suggestion: {
      items: ({ query }: { query: string }) => lookupFn(query),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (): any => {
        let component: ReactRenderer | null = null;
        let popup: Instance[] | null = null;

        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onStart: (props: any) => {
            component = new ReactRenderer(MentionSuggestionList as any, {
              props: {
                items: props.items as MentionItem[],
                command: props.command as (item: MentionItem) => void,
              },
              editor: props.editor,
            });

            if (!props.clientRect) return;
            popup = tippy('body', {
              getReferenceClientRect: props.clientRect as () => DOMRect,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            });
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onUpdate: (props: any) => {
            component?.updateProps({
              items: props.items as MentionItem[],
              command: props.command as (item: MentionItem) => void,
            });
            if (popup && props.clientRect) {
              popup[0]?.setProps({
                getReferenceClientRect: props.clientRect as () => DOMRect,
              });
            }
          },
          onKeyDown: (props: { event: KeyboardEvent }) => {
            if (props.event.key === 'Escape') {
              popup?.[0]?.hide();
              return true;
            }
            return (
              (component?.ref as MentionSuggestionListRef | null)?.onKeyDown(
                props,
              ) ?? false
            );
          },
          onExit: () => {
            popup?.[0]?.destroy();
            component?.destroy();
          },
        };
      },
    },
  });
}
